import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http'
import { inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { ErrorCode, ErrorCodes } from '@taketheme/error-codes'
import { Observable, catchError, from, switchMap, throwError } from 'rxjs'
import { getPlanName } from '../constants/plan-catalog'

const knownCodes = new Set<string>(Object.values(ErrorCodes))

/**
 * Client-side pseudo-code stamped on `_originalCode` when a request never got
 * an HTTP response (status 0): the device is offline, DNS/TLS failed, the API
 * is down, or CORS rejected the reply. The backend never emits it, so it is
 * deliberately not in @taketheme/error-codes.
 */
export const NETWORK_ERROR_CODE = 'NETWORK_ERROR'

/** Same response, different body — everything callers switch on (status, url, headers) is preserved. */
function withBody(error: HttpErrorResponse, body: Record<string, unknown>): HttpErrorResponse {
  return new HttpErrorResponse({
    error: body,
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url ?? undefined,
  })
}

/**
 * A status-0 failure has no JSON body: `error.error` is the XHR ProgressEvent
 * and `error.message` is Angular's English "Http failure response for <url>:
 * 0 Unknown Error". Callers that toast `err.error.message` rendered a BLANK
 * toast and callers that toast `err.message` rendered English regardless of
 * the dashboard language. Give it the same shape as a translated coded error
 * so every existing call site shows a translated, actionable message.
 */
function translateNetworkError(error: HttpErrorResponse, translate: TranslateService): Observable<never> {
  // navigator.onLine is only trustworthy when false: true just means "has a
  // network interface", so that side gets the more general wording.
  const key = navigator.onLine ? 'error.networkUnreachable' : 'error.networkOffline'
  return throwError(() => withBody(error, { message: translate.instant(key), _originalCode: NETWORK_ERROR_CODE }))
}

/**
 * Plan-gate 403s (FEATURE_NOT_AVAILABLE, STAFF_LIMIT_REACHED, …) carry the
 * plan that lifts them as `requiredPlan`; `error.withPlan.<CODE>` turns that
 * into "Upgrade to Pro…" instead of "requires a higher plan". Null when the
 * body names no plan or the code has no plan-naming copy.
 */
function translateWithPlan(code: string, body: any, translate: TranslateService): string | null {
  const plan = typeof body?.requiredPlan === 'string' ? getPlanName(body.requiredPlan, translate.currentLang || 'en') : null
  if (!plan) return null
  const key = `error.withPlan.${code}`
  const translated = translate.instant(key, { plan })
  return translated && translated !== key ? translated : null
}

/** Swaps a coded error's message for its translation (or a generic fallback), else passes it through untouched. */
function translateError(error: HttpErrorResponse, translate: TranslateService): Observable<never> {
  const code: string = error.error?.message

  const translationKey = `error.${code}`
  const translated = translateWithPlan(code, error.error, translate) ?? translate.instant(translationKey)

  if (translated && translated !== translationKey) {
    return throwError(() => withBody(error, { ...error.error, message: translated, _originalCode: code }))
  }

  if (knownCodes.has(code)) {
    const fallback = translate.instant('error.somethingWentWrong')
    return throwError(() => withBody(error, { ...error.error, message: fallback, _originalCode: code as ErrorCode }))
  }

  return throwError(() => error)
}

export const errorTranslationInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
  const translate = inject(TranslateService)

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!(error instanceof HttpErrorResponse) || error.status === 402) {
        return throwError(() => error)
      }

      if (error.status === 0) {
        return translateNetworkError(error, translate)
      }

      // A `responseType: 'blob'` request (file exports/downloads, e.g. Products
      // → Export) still gets its ERROR body delivered as a Blob rather than
      // parsed JSON. error.error.message is then undefined, so this used to
      // fall straight through as Angular's generic "Http failure response for
      // <url>: <status> <statusText>" -- no matter what structured, coded error
      // the API actually sent. Read it as text and parse it like a normal body
      // before applying the same translation logic.
      if (error.error instanceof Blob && error.error.type === 'application/json') {
        return from(error.error.text()).pipe(
          switchMap(text => {
            let parsedBody: any
            try {
              parsedBody = JSON.parse(text)
            } catch {
              return throwError(() => error)
            }
            if (!parsedBody?.message) return throwError(() => error)
            return translateError(withBody(error, parsedBody), translate)
          }),
        )
      }

      if (!error.error?.message) {
        return throwError(() => error)
      }

      return translateError(error, translate)
    }),
  )
}
